import { useCallback, useEffect, useRef, useState } from 'react';

const PREFIX = 'zmist.ui.';

function read<T>(key: string, fallback: T, isValid?: (v: unknown) => boolean): T {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw);
    return !isValid || isValid(parsed) ? (parsed as T) : fallback;
  } catch {
    return fallback;
  }
}

// useState that survives reloads: the value is mirrored to localStorage under
// "zmist.ui.<key>" and re-read when `key` changes (e.g. per-channel keys).
// `isValid` guards against stale/foreign stored values (a renamed tab, a
// removed option) - an invalid one falls back to `initial`. Storage may be
// unavailable (private mode, quota) - every access is best-effort, the state
// still works in memory.
export function usePersistedState<T>(key: string, initial: T, isValid?: (v: unknown) => boolean) {
  const [value, setValue] = useState<T>(() => read(key, initial, isValid));
  const keyRef = useRef(key);

  useEffect(() => {
    if (keyRef.current === key) return;
    keyRef.current = key;
    setValue(read(key, initial, isValid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        try {
          localStorage.setItem(PREFIX + keyRef.current, JSON.stringify(resolved));
        } catch {
          // best-effort
        }
        return resolved;
      });
    },
    []
  );
  return [value, set] as const;
}

export const oneOf = <T extends string>(allowed: readonly T[]) => (v: unknown): boolean => allowed.includes(v as T);
export const stringArray = (v: unknown): boolean => Array.isArray(v) && v.every((x) => typeof x === 'string');
